import { useEffect, useRef, useState } from "react";
import type { CncMachineProfile, CncSeatStandard, CncToolDef } from "../../../shared/api-contract";
import { Icon } from "../../kit";
import {
  CNC_SEAT_CUTS,
  draftFromRow,
  draftWithCut,
  emptySeatDraft,
  findSeatRow,
  formatSeatMM,
  isShapedSeat,
  rowFromDraft,
  seatCutLabel,
  seatCutOf,
  seatRowName,
  seatTableOrder,
  seatToolChoices,
  seatToolLabel,
  withSeatRow,
  withoutSeatRow,
} from "./cncSeats";
import type { SeatDraft } from "./cncSeats";
import { Banner, CheckRow, Choice, CncSection, LabeledText, SmallButton } from "./cncUi";

/**
 * "Stone seats" — the machine profile's seat table, as a jeweller and the
 * shop's machinist read it: for each stone, the cutter that cuts its seat (or,
 * for a shaped stone, the end mill that pockets round it), how deep, and the
 * drill for the light hole under it.
 *
 * Why it is its own section: a stone size the table does not hold is a GUESS
 * scaled from the shop's 2.8 mm row, and the viewer withholds every program
 * that uses a guess (`seat.derived`). Entering the shop's numbers here is the
 * only thing that unblocks another size. A `seat.derived` fail in the checks
 * opens this editor prefilled with the guess (`request`), so the shop confirms
 * or corrects numbers instead of typing them from nothing.
 *
 * Every save goes through the dialog's `onCommit` — `validateCncProfile`, then
 * the profile store in use (the workspace or this browser) — and a saved row
 * is always the SHOP's, which is why Save waits for an explicit "these are
 * the shop's numbers".
 */
export function CncSeatTable({
  profile,
  onCommit,
  request,
  storeNote,
}: {
  profile: CncMachineProfile;
  /** Replace the profile in use; the reasons it was refused, or null when it was used. */
  onCommit: (next: CncMachineProfile) => string[] | null;
  /** A guessed row to open the editor on (a `seat.derived` check's button); `key` changes per click. */
  request: { row: CncSeatStandard; key: number } | null;
  /** Where a saved change goes, in a few words. */
  storeNote: string;
}) {
  const [editing, setEditing] = useState<{ index: number | null; draft: SeatDraft; fromGuess: boolean; confirmed: boolean } | null>(null);
  const [errors, setErrors] = useState<string[] | null>(null);
  const [removing, setRemoving] = useState<number | null>(null);
  const root = useRef<HTMLDivElement>(null);

  // Another profile picked: an open edit belonged to the old one.
  useEffect(() => { setEditing(null); setErrors(null); setRemoving(null); }, [profile.name]);

  // A `seat.derived` check asked for this row: open it prefilled, over the row already stored for that stone if any.
  useEffect(() => {
    if (!request) return;
    const at = findSeatRow(profile, request.row);
    setEditing({ index: at >= 0 ? at : null, draft: draftFromRow(request.row), fromGuess: true, confirmed: false });
    setErrors(null);
    setRemoving(null);
    root.current?.scrollIntoView({ block: "start", behavior: "smooth" });
    // Only a new request (its key) reopens the editor; the profile changing under it does not.
  }, [request?.key]);

  const order = seatTableOrder(profile);
  const guesses = profile.seatStandard.filter((r) => r.source !== "shop").length;

  const open = (index: number | null) => {
    const row = index === null ? null : profile.seatStandard[index];
    setEditing({ index, draft: row ? draftFromRow(row) : emptySeatDraft(profile), fromGuess: !!row && row.source !== "shop", confirmed: false });
    setErrors(null);
    setRemoving(null);
  };

  const save = () => {
    if (!editing) return;
    const read = rowFromDraft(editing.draft, profile);
    if ("errors" in read) return setErrors(read.errors);
    const next = withSeatRow(profile, read.row, editing.index);
    if ("error" in next) return setErrors([next.error]);
    const refused = onCommit(next.profile);
    if (refused) return setErrors(refused);
    setEditing(null);
    setErrors(null);
  };

  const remove = (index: number) => {
    if (removing !== index) return setRemoving(index);
    const refused = onCommit(withoutSeatRow(profile, index));
    setRemoving(null);
    if (refused) setErrors(refused);
    else if (editing?.index === index) setEditing(null);
  };

  return (
    <div ref={root} className="scroll-mt-4">
      <CncSection title="Stone seats" aside={<span data-testid="cnc-seats-store" className="font-body text-[11px] text-ed-text-3">{storeNote}</span>}>
        <span className="font-body text-[11px] text-ed-text-3">
          How the shop cuts the seat under each stone: the cutter, how deep, and the drill for the light hole. A stone size that
          is not in this table is our guess from the shop&rsquo;s 2.8 mm seat, and no program is made with a guess — enter the
          shop&rsquo;s numbers here to unblock it.
        </span>

        <ul className="flex flex-col gap-1.5" data-testid="cnc-seats">
          {order.map((i) => {
            const row = profile.seatStandard[i];
            const shop = row.source === "shop";
            return (
              <li key={`${seatCutOf(row)}-${row.stoneMM}-${i}`} data-testid="cnc-seat-row"
                className={`flex flex-col gap-1 rounded-md border px-3 py-2 font-body text-[12px] ${shop ? "border-ed-border bg-ed-panel" : "border-amber-300 bg-amber-50"}`}>
                <span className="flex items-baseline justify-between gap-2">
                  <span className="font-medium text-ed-text">{seatCutLabel(seatCutOf(row))} {formatSeatMM(row.stoneMM)} mm</span>
                  <span className={`shrink-0 text-[11px] ${shop ? "text-ed-text-3" : "font-medium text-amber-900"}`}>
                    {shop ? "The shop's" : "Our guess — blocks the program"}
                  </span>
                </span>
                <span className="text-ed-text-2">{rowSummary(row, profile.tools)}</span>
                <span className="flex flex-wrap gap-2">
                  <SmallButton testId="cnc-seat-edit" onClick={() => open(i)}>
                    <Icon name="edit" size={14} /> {shop ? "Change" : "Enter the shop's numbers"}
                  </SmallButton>
                  <SmallButton testId="cnc-seat-remove" tone="danger" onClick={() => remove(i)}>
                    {removing === i ? "Click again to remove" : "Remove"}
                  </SmallButton>
                  {removing === i ? <SmallButton onClick={() => setRemoving(null)}>Keep it</SmallButton> : null}
                </span>
                {removing === i && shop ? (
                  <span className="text-[11px] text-ed-text-3">Programs for this stone are blocked again until its seat is entered.</span>
                ) : null}
              </li>
            );
          })}
          {order.length === 0 ? <li className="font-body text-[12px] text-ed-text-3">The table is empty: every stone would be a guess.</li> : null}
        </ul>
        {guesses > 0 ? (
          <span className="font-body text-[11px] text-amber-900">
            {guesses === 1 ? "One row is" : `${guesses} rows are`} still our guess — a program using {guesses === 1 ? "it" : "one"} is withheld.
          </span>
        ) : null}

        {editing ? (
          <SeatEditor
            profile={profile}
            editing={editing}
            errors={errors}
            onDraft={(draft) => setEditing({ ...editing, draft })}
            onConfirm={(confirmed) => setEditing({ ...editing, confirmed })}
            onSave={save}
            onCancel={() => { setEditing(null); setErrors(null); }}
          />
        ) : (
          <div>
            <SmallButton testId="cnc-seat-add" onClick={() => open(null)}>
              <Icon name="add" size={14} /> Add a stone size
            </SmallButton>
          </div>
        )}
        {!editing && errors ? (
          <Banner tone="fail" icon="error" testId="cnc-seat-error">
            <span className="font-semibold">That change was not applied.</span>
            <ul className="mt-1 list-disc pl-4">{errors.map((e) => <li key={e}>{e}</li>)}</ul>
          </Banner>
        ) : null}
      </CncSection>
    </div>
  );
}

/** One row in words: "Seat: <cutter> to 1.46 mm · Light hole: <drill> to 1.76 mm". */
function rowSummary(row: CncSeatStandard, tools: readonly CncToolDef[]): string {
  const name = (id: string | null) => (id ? tools.find((t) => t.id === id)?.label ?? id : "");
  const cut = isShapedSeat(row)
    ? `Pocket: ${name(row.seatToolId)} to ${formatSeatMM(row.seatDepthMM)} mm, ${formatSeatMM(row.clearanceMM ?? 0)} mm clear of the stone`
    : `Seat: ${name(row.seatToolId)} to ${formatSeatMM(row.seatDepthMM)} mm`;
  const hole = row.drillToolId ? `Light hole: ${name(row.drillToolId)} to ${formatSeatMM(row.drillDepthMM)} mm` : "No light hole";
  return `${cut} · ${hole}`;
}

const selectCls =
  "w-full rounded-md border border-ed-line/60 bg-ed-panel px-2.5 py-1.5 font-body text-[12px] text-ed-text outline-none focus:border-ed-accent disabled:opacity-50";

function SeatEditor({
  profile, editing, errors, onDraft, onConfirm, onSave, onCancel,
}: {
  profile: CncMachineProfile;
  editing: { index: number | null; draft: SeatDraft; fromGuess: boolean; confirmed: boolean };
  errors: string[] | null;
  onDraft: (d: SeatDraft) => void;
  onConfirm: (v: boolean) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const { draft } = editing;
  const shaped = draft.cut !== "round";
  const cutters = seatToolChoices(profile, shaped ? "pocket" : "seat");
  const drills = seatToolChoices(profile, "drill");
  const set = (patch: Partial<SeatDraft>) => onDraft({ ...draft, ...patch });
  // A shop row can still name a cutter or drill the shop has not confirmed: say so before the box is ticked.
  const unconfirmed = [...cutters, ...drills].filter(
    (t, i, all) => (t.id === draft.seatToolId || t.id === draft.drillToolId) && t.source !== "shop" && all.indexOf(t) === i,
  );
  const cuts = CNC_SEAT_CUTS.some((c) => c.id === draft.cut) ? CNC_SEAT_CUTS : [...CNC_SEAT_CUTS, { id: draft.cut, label: seatCutLabel(draft.cut) }];
  const stored = editing.index === null ? null : profile.seatStandard[editing.index];
  const sizeNum = Number(draft.stoneMM);
  const title = editing.fromGuess
    ? `The shop's seat for ${Number.isFinite(sizeNum) && sizeNum > 0 ? seatRowName({ cut: draft.cut, stoneMM: sizeNum }) : "this stone"}`
    : stored ? `Change the seat for ${seatRowName(stored)}` : "A new stone seat";

  return (
    <div data-testid="cnc-seat-editor" className="flex flex-col gap-2.5 rounded-lg border border-ed-accent bg-ed-panel-2 p-3">
      <span className="font-body text-[12px] font-semibold text-ed-text">{title}</span>
      {editing.fromGuess ? (
        <Banner tone="warn" icon="edit_note" testId="cnc-seat-prefilled">
          These numbers are <span className="font-semibold">our guess</span>, scaled from the shop&rsquo;s 2.8 mm seat. Change each one to
          what the shop actually uses before you save.
        </Banner>
      ) : null}

      <div className="flex flex-col gap-1">
        <span className="font-body text-[11px] font-medium text-ed-text-2">Stone shape</span>
        <Choice<string> testId="cnc-seat-cut" options={cuts} value={draft.cut} onChange={(cut) => onDraft(draftWithCut(draft, cut, profile))} />
      </div>
      <LabeledText testId="cnc-seat-size" label="Stone size (mm)" value={draft.stoneMM} onChange={(v) => set({ stoneMM: v })}
        placeholder={shaped ? "e.g. 4.9" : "e.g. 3.0"}
        hint={shaped ? "As the designer sizes a shaped stone: √(length × width). A 6 × 4 mm oval is 4.9." : "The stone's diameter at its girdle."} />

      <label className="flex flex-col gap-1">
        <span className="font-body text-[11px] font-medium text-ed-text-2">{shaped ? "Pocket cutter (flat end mill)" : "Seat cutter"}</span>
        <select data-testid="cnc-seat-cutter" className={selectCls} value={draft.seatToolId} onChange={(e) => set({ seatToolId: e.target.value })}>
          {cutters.length === 0 ? <option value="">{shaped ? "No flat end mill in this profile" : "No cutter in this profile"}</option> : null}
          {cutters.map((t) => <option key={t.id} value={t.id}>{seatToolLabel(t)}</option>)}
        </select>
      </label>
      <div className="flex gap-3">
        <LabeledText testId="cnc-seat-depth" label={shaped ? "Pocket depth (mm)" : "Seat depth (mm)"} value={draft.seatDepthMM}
          onChange={(v) => set({ seatDepthMM: v })} hint="Below the top of the band." />
        {shaped ? (
          <LabeledText testId="cnc-seat-clearance" label="Clearance (mm)" value={draft.clearanceMM}
            onChange={(v) => set({ clearanceMM: v })} hint="Gap between the stone and the pocket wall." />
        ) : null}
      </div>

      <div className="flex gap-3">
        <label className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="font-body text-[11px] font-medium text-ed-text-2">Light-hole drill</span>
          <select data-testid="cnc-seat-drill" className={selectCls} value={draft.drillToolId} onChange={(e) => set({ drillToolId: e.target.value })}>
            <option value="">No light hole</option>
            {drills.map((t) => <option key={t.id} value={t.id}>{seatToolLabel(t)}</option>)}
          </select>
        </label>
        {draft.drillToolId ? (
          <LabeledText testId="cnc-seat-drill-depth" label="Hole depth (mm)" value={draft.drillDepthMM}
            onChange={(v) => set({ drillDepthMM: v })} hint="Below the top of the band." />
        ) : null}
      </div>
      <span className="font-body text-[11px] text-ed-text-3">
        Only the cutters and drills in this machine profile are offered. One marked &ldquo;not confirmed by the shop&rdquo; makes
        every program that uses it say so.
      </span>
      {unconfirmed.length ? (
        <Banner tone="warn" icon="build" testId="cnc-seat-unconfirmed">
          <span className="font-semibold">{unconfirmed.map((t) => t.label).join(" and ")}</span>{" "}
          {unconfirmed.length > 1 ? "are" : "is"} not among the shop&rsquo;s confirmed tools. The tool number, work offset and feeds
          printed for {unconfirmed.length > 1 ? "them" : "it"} are our placeholders, so ticking the box below confirms the seat
          numbers only: every program for this stone stays marked &ldquo;tool not confirmed&rdquo; until the shop confirms{" "}
          {unconfirmed.length > 1 ? "these tools" : "this tool"}.
        </Banner>
      ) : null}

      <CheckRow testId="cnc-seat-confirm" checked={editing.confirmed} onChange={onConfirm}
        label="These are the shop's own numbers for this stone"
        hint="Saving marks the row as the shop's: programs for this stone are then made with it, no longer blocked as a guess." />

      {errors ? (
        <Banner tone="fail" icon="error" testId="cnc-seat-error">
          <span className="font-semibold">Not saved — fix these first:</span>
          <ul className="mt-1 list-disc pl-4">{errors.map((e) => <li key={e}>{e}</li>)}</ul>
        </Banner>
      ) : null}
      <div className="flex gap-2">
        <SmallButton testId="cnc-seat-save" tone="ink" disabled={!editing.confirmed} onClick={onSave}
          title={editing.confirmed ? undefined : "Tick the box above first"}>
          Save to the table
        </SmallButton>
        <SmallButton onClick={onCancel}>Cancel</SmallButton>
      </div>
    </div>
  );
}
